import {
  clearLaborCategoryPdtMetadata,
  confirmLaborCategoryPdtUpload,
  getLaborCategoryPdtDownloadUrl,
  requestLaborCategoryPdtUpload,
  uploadLaborCategoryPdtToS3,
} from '@access-cms/api-client';

import type { UploadedFile } from '../../../components/FileUpload';
import type { LaborCategoryData } from '../components/LaborCategoryModal';

export const EXISTING_PDT_FILE_ID = 'existing-pdt-file';

const PDT_MIME_TYPE = 'application/pdf';

function shouldSkipPdtUpload(data: LaborCategoryData): boolean {
  const uploadedFile = data.files[0];
  if (!uploadedFile?.name?.trim()) {
    return true;
  }

  return uploadedFile.id === EXISTING_PDT_FILE_ID && (!uploadedFile.file || uploadedFile.file.size === 0);
}

/**
 * Open a locally selected PDT in a new browser tab (before S3 upload).
 */
export function openLocalPdtInNewTab(file: File): void {
  const objectUrl = URL.createObjectURL(file);
  // Do not pass `noopener` in windowFeatures: browsers then return null even when the
  // tab opens successfully, which falsely triggers the error alert below.
  const pdtWindow = window.open(objectUrl, '_blank');

  if (!pdtWindow) {
    URL.revokeObjectURL(objectUrl);
    throw new Error('Unable to open a new browser tab for the PDT file.');
  }

  pdtWindow.opener = null;

  // Revoke after the tab has a chance to load the blob URL.
  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 60_000);
}

/**
 * Open a PDT stored in S3 via a short-lived presigned download URL.
 */
export async function openRemotePdtInNewTab(contractId: string, laborCategoryId: string): Promise<void> {
  const pdtWindow = window.open('about:blank', '_blank');
  if (pdtWindow) {
    pdtWindow.opener = null;
  }

  try {
    const response = await getLaborCategoryPdtDownloadUrl(contractId, laborCategoryId);
    const downloadUrl = response.data?.downloadUrl;

    if (!downloadUrl) {
      throw new Error('PDT download URL was not returned.');
    }

    if (pdtWindow) {
      pdtWindow.location.href = downloadUrl;
    } else {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    pdtWindow?.close();
    throw error;
  }
}

/**
 * Open a PDT from either a local File (pre-upload) or the remote labor-category PDT.
 */
export async function openLaborCategoryPdt(
  file: UploadedFile,
  options: { contractId: string; laborCategoryId?: string },
): Promise<void> {
  if (file.file && file.file.size > 0) {
    openLocalPdtInNewTab(file.file);

    return;
  }

  if (!options.laborCategoryId) {
    throw new Error('This PDT is not available yet.');
  }

  await openRemotePdtInNewTab(options.contractId, options.laborCategoryId);
}

/**
 * PDT flow after labor category create/update:
 * 1. Request presigned upload URL and reserved S3 key (backend)
 * 2. Upload file to S3 via presigned PUT
 * 3. Confirm upload so pdtUploadedAt is persisted (backend)
 */
export async function uploadLaborCategoryPdtAfterSave(
  contractId: string,
  laborCategoryId: string,
  data: LaborCategoryData,
): Promise<void> {
  if (shouldSkipPdtUpload(data)) {
    return;
  }

  const uploadedFile = data.files[0];
  const file = uploadedFile.file;
  const pdtFileName = uploadedFile.name.trim();

  if (!file || file.size === 0) {
    throw new Error('A Position Designation Tool (PDT) file is required.');
  }

  const uploadRequest = await requestLaborCategoryPdtUpload(contractId, laborCategoryId, {
    mimeType: PDT_MIME_TYPE,
    pdtFileName,
    sizeBytes: file.size,
  });

  const uploadData = uploadRequest.data;
  if (!uploadData?.uploadUrl) {
    throw new Error('PDT upload request did not return an upload URL');
  }

  try {
    await uploadLaborCategoryPdtToS3(uploadData.uploadUrl, file);
    await confirmLaborCategoryPdtUpload(contractId, laborCategoryId);
  } catch (error) {
    console.error('[PDT] Labor category PDT upload flow failed', {
      contractId,
      error: error instanceof Error ? error.message : String(error),
      laborCategoryId,
    });
    try {
      await clearLaborCategoryPdtMetadata(contractId, laborCategoryId);
    } catch (clearError) {
      console.error('[PDT] Failed to clear labor category PDT metadata after upload failure:', clearError);
    }

    throw error;
  }
}
